import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AgoraRTC, {
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteAudioTracks,
  useRemoteUsers,
} from "agora-rtc-react";

import { getGeneratedAgoraToken, validateMeetingToken } from "@/api/token";
import {
  sendAstrologerHeartbeat,
  endBillingSessionAstrologer,
  getBillingStatusPartner,
  updateMeetingState,
} from "@/api/calls";
import { getMeetingById } from "@/api/services";

import { useSocketEmitter, useSocketReceiver } from "./use-sockets";
import { errorToast, toastInfo } from "@/lib/toast";
import { useAuth } from "./use-auth";

const appid = import.meta.env.VITE_AGORA_APP_ID;
const uid = 1;

const HEARTBEAT_INTERVAL_MS = 10_000;
const BILLING_POLL_INTERVAL_MS = 15_000;

/**
 * useScheduleCall — partner-app's scheduled-meeting in-call hook.
 *
 * Mirrors the mobile/as-webapp counterpart's lifecycle on the
 * astrologer side. Major changes vs the pre-D1 version:
 *
 *   - Pulls the actual meeting (user name + mode) via /api/meeting/
 *     by-id/:id instead of guessing from `appointments[0]`. The old
 *     approach picked up the wrong meeting whenever the astrologer
 *     had multiple pending bookings.
 *
 *   - Sends partner-side heartbeats every 10s so the server's watchdog
 *     can detect THIS side disconnecting (not just the user side).
 *     Without this, an astrologer dropping mid-call wouldn't trigger
 *     the user's auto-refund — the server would only see the user-
 *     side heartbeats keep firing.
 *
 *   - Drives a wall-clock duration counter via Date.now() so the
 *     screen can show "X minutes elapsed" without depending on
 *     onRtcStats (which was inconsistent on partner-app's agora-rtc-
 *     react setup).
 *
 *   - Polls billing status (15s) so the astrologer-side UI can show
 *     "earned so far" (partner-scoped — no user wallet exposure).
 *
 *   - `endCall(reason)` is the canonical leave path: stops timers,
 *     calls endBilling (server flips Meeting → completed via the
 *     meetingState service), defensively also calls updateMeetingState,
 *     navigates to meeting-end. Idempotent.
 */
export const useScheduleCall = (mode) => {
  const { meetingId } = useParams();
  const navigate = useNavigate();

  const { astrologer } = useAuth();

  const [isPageReady, setIsPageReady] = useState(false);
  const [isUserReady, setIsUserReady] = useState(true);
  const [token, setToken] = useState(null);
  const [channel, setChannel] = useState(null);
  const [micOn, setMic] = useState(true);
  const [cameraOn, setCamera] = useState(mode === "video" ? true : false);
  const [isFrontFacing, setIsFrontFacing] = useState(true);
  const [cameraDevices, setCameraDevices] = useState([]);
  const [microphoneDevices, setMicrophoneDevices] = useState([]);
  const [speakerDevices, setSpeakerDevices] = useState([]);

  // Meeting context — populated from /api/meeting/by-id. Keyed off
  // meetingId from the URL, so the screen always shows the right
  // user even when the astrologer has multiple pending bookings.
  const [meeting, setMeeting] = useState(null);

  // Wall-clock duration in seconds, shown in the in-call UI and sent
  // to updateMeetingState on leave. Starts ticking when isConnected
  // flips true.
  const [duration, setDuration] = useState(0);

  // Partner-scoped billing snapshot. We surface `earnedSoFar` (₹)
  // and `billedMinutes` to the in-call UI so the astrologer can see
  // their earnings accrue live.
  const [billing, setBilling] = useState({
    isActive: false,
    ratePerMinute: 0,
    totalBilledAmount: 0,
    totalBilledMinutes: 0,
  });

  // Idempotency + timer refs.
  const heartbeatRef = useRef(null);
  const billingPollRef = useRef(null);
  const durationStartedAtRef = useRef(null);
  const durationTickerRef = useRef(null);
  const hasCleanedUpRef = useRef(false);

  const { emitter } = useSocketEmitter();

  const { isConnected, isLoading } = useJoin(
    { channel, token, uid, appid },
    token && isPageReady && isUserReady
  );

  const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn);
  const { localCameraTrack } = useLocalCameraTrack(
    mode === "video" ? cameraOn : false,
    { facingMode: "user" }
  );

  usePublish(
    [localMicrophoneTrack, localCameraTrack],
    isPageReady && isUserReady
  );

  const remoteUsers = useRemoteUsers();

  const { audioTracks } = useRemoteAudioTracks(remoteUsers);
  audioTracks.map((a) => a.play());

  useEffect(() => {
    emitter("status-update", { isActive: !isConnected });
  }, [isConnected]);

  // User clicked End on their side — server pushes 'disconnect-call' to
  // the partner socket. Tear down our side cleanly with reason
  // 'remote_user_left'. Server will already have flipped the meeting
  // and ended billing from the user's endBillingSession call.
  useSocketReceiver("disconnect-call", () => {
    endCall("remote_user_left").finally(() => {
      // Existing flow returned to "/" — preserve that.
      navigate("/", { replace: true, viewTransition: true });
    });
  });

  useEffect(() => {
    fetchDevices();
  }, []);

  useEffect(() => {
    if (token && channel) setIsPageReady(true);
    else setIsPageReady(false);
  }, [token, channel]);

  useEffect(() => {
    if (astrologer.astrologerId) {
      validateMeeting();
    }
  }, [meetingId, astrologer.astrologerId]);

  useEffect(() => {
    if (!channel) return;
    fetchToken();
  }, [channel]);

  // Fetch the meeting details once the channel resolves. Used by the
  // call screens to show the user's name + the booked mode. Failing
  // to fetch isn't fatal — the screen falls back to URL/empty defaults.
  useEffect(() => {
    if (!meetingId) return;
    let cancelled = false;
    (async () => {
      const m = await getMeetingById(meetingId);
      if (!cancelled) setMeeting(m);
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  // ---- Heartbeat -----------------------------------------------------
  // Fires every 10s while isConnected so the server's billing-engine
  // watchdog sees the astrologer side as alive. Without this, the
  // watchdog would only see user-side heartbeats and an astrologer-
  // initiated drop would never trigger the user's partner-disconnect
  // auto-refund.
  useEffect(() => {
    if (!isConnected || !channel) return;
    const fire = () => sendAstrologerHeartbeat(channel);
    fire();
    heartbeatRef.current = setInterval(fire, HEARTBEAT_INTERVAL_MS);
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [isConnected, channel]);

  // ---- Wall-clock duration ticker -----------------------------------
  // Starts when isConnected flips true; stops on cleanup. Drives the
  // "X min Y sec" display + the duration sent to updateMeetingState
  // on leave (belt-and-braces; the server's endBilling also writes
  // its own duration from the BillingSession).
  useEffect(() => {
    if (!isConnected) return;
    durationStartedAtRef.current = Date.now();
    durationTickerRef.current = setInterval(() => {
      const elapsed = Math.floor(
        (Date.now() - durationStartedAtRef.current) / 1000
      );
      setDuration(elapsed);
    }, 1000);
    return () => {
      if (durationTickerRef.current) {
        clearInterval(durationTickerRef.current);
        durationTickerRef.current = null;
      }
    };
  }, [isConnected]);

  // ---- Billing poll -------------------------------------------------
  // Partner-scoped — server omits the user's live wallet snapshot.
  // Used by the UI to show the astrologer's earnings as they accrue.
  useEffect(() => {
    if (!isConnected || !channel) return;
    const poll = async () => {
      const status = await getBillingStatusPartner(channel);
      if (!status) return;
      setBilling({
        isActive: !!status.active,
        ratePerMinute: status.ratePerMinute ?? 0,
        totalBilledAmount: status.totalBilledAmount ?? 0,
        totalBilledMinutes: status.totalDurationMinutes ?? 0,
      });
    };
    poll();
    billingPollRef.current = setInterval(poll, BILLING_POLL_INTERVAL_MS);
    return () => {
      if (billingPollRef.current) {
        clearInterval(billingPollRef.current);
        billingPollRef.current = null;
      }
    };
  }, [isConnected, channel]);

  const fetchToken = async () => {
    const token = await getGeneratedAgoraToken(channel, uid, {
      userId: astrologer?.astrologerId,
      userType: "astrologer",
    });
    token && setToken(token);
  };

  const validateMeeting = async () => {
    try {
      if (!meetingId) {
        errorToast("Invalid Meeting Link!");
        return navigate("/", { replace: true, viewTransition: true });
      }
      const { isValid, message, meeting } = await validateMeetingToken(
        meetingId,
        astrologer.astrologerId
      );

      if (!isValid) {
        toastInfo(message);
        // return navigate("/", { replace: true, viewTransition: true });
        return window.history.back();
      }
      setChannel(meeting._id);
    } catch (err) {
      errorToast("Something went wrong!");
      navigate("/", { replace: true, viewTransition: true });
      console.error(err);
    }
  };

  const fetchDevices = async () => {
    try {
      const cameras = await AgoraRTC.getCameras(false);
      const microphones = await AgoraRTC.getMicrophones(false);
      const speakers = await AgoraRTC.getPlaybackDevices(false);

      const cameraGroupIds = [];
      const microphoneGroupIds = [];
      const uniqueCameras = cameras.filter((c) => {
        if (!cameraGroupIds.includes(c.groupId)) {
          cameraGroupIds.push(c.groupId);
          return c;
        }
      });
      const uniqueMicrophones = microphones.filter((m) => {
        if (!microphoneGroupIds.includes(m.groupId)) {
          microphoneGroupIds.push(m.groupId);
          return m;
        }
      });

      const uniqueSpeakers = speakers.filter((s) => {
        if (!cameraGroupIds.includes(s.groupId)) {
          cameraGroupIds.push(s.groupId);
          return s;
        }
      });

      setCameraDevices(uniqueCameras);
      setMicrophoneDevices(uniqueMicrophones);
      setSpeakerDevices(uniqueSpeakers);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCameraDeviceChange = async (e) => {
    const deviceId = e.target.value;
    await localCameraTrack.setDevice({ deviceId });
  };

  const handleMicrophoneDeviceChange = async (e) => {
    const deviceId = e.target.value;
    await localMicrophoneTrack.setDevice(deviceId);
  };

  const handleSpeakerDeviceChange = async (e) => {
    const deviceId = e.target.value;
    audioTracks.forEach(async (a) => await a.setPlaybackDevice(deviceId));
  };

  const handleCameraSwitch = async () => {
    await localCameraTrack.setDevice({
      facingMode: isFrontFacing ? "environment" : "user",
    });
    setIsFrontFacing((prev) => !prev);
  };

  /**
   * Ordered teardown for the astrologer side. Idempotent via
   * hasCleanedUpRef.
   *
   *   1. Stop timers — no more heartbeats / polls / ticks
   *   2. End the billing session — server flips Meeting → completed
   *      via meetingState service AND records final billed amount
   *   3. Defensive updateMeetingState — covers the case where billing
   *      was never started (e.g. user joined and left before astrologer
   *      arrived; partner clicks End to clear it out)
   *   4. Set isUserReady=false to detach Agora publish + leave channel
   *   5. Emit status-update so the user-side sees the partner offline
   *
   * Navigation is left to the caller. The voice/video screens already
   * have their own post-end navigation, and the socket-disconnect path
   * navigates differently from the manual-end path.
   *
   * reason: matches the server's endReason enum members ('astrologer_
   * ended', 'astrologer_disconnected', 'remote_user_left', etc).
   */
  const endCall = async (reason = "astrologer_ended") => {
    if (hasCleanedUpRef.current) return;
    hasCleanedUpRef.current = true;

    // Stop timers immediately so no late heartbeats fire after end.
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (billingPollRef.current) {
      clearInterval(billingPollRef.current);
      billingPollRef.current = null;
    }
    if (durationTickerRef.current) {
      clearInterval(durationTickerRef.current);
      durationTickerRef.current = null;
    }

    const finalDuration = duration;

    // Attribution mapping — when the astrologer ends voluntarily,
    // attribution stays 'astrologer' but endReason='astrologer_ended'
    // does NOT match the server's refund-trigger reasons, so the
    // user is not auto-refunded. That's correct: a clean hangup
    // shouldn't trigger a refund. The refund path triggers on
    // 'astrologer_disconnected' / 'network_timeout' / 'orphan_sweeper'
    // only.
    const attribution =
      reason === "remote_user_left"
        ? "user"
        : reason === "network_timeout" || reason === "connection_lost"
        ? "network"
        : "astrologer";

    if (channel) {
      await endBillingSessionAstrologer(channel, reason, attribution);

      // Defensive: covers the case where billing wasn't started
      // (no session existed) and we still want the Meeting status
      // to flip from 'pending' to 'completed' so the appointment
      // doesn't linger. Idempotent on already-terminal meetings.
      await updateMeetingState(meetingId, "completed", finalDuration);
    }

    // Detach Agora — flipping isUserReady=false stops publish and
    // triggers useJoin to leave the channel. The agora-rtc-react
    // useJoin / usePublish hooks own the actual cleanup.
    setIsUserReady(false);
    emitter("status-update", { isActive: true });
  };

  return {
    cameraDevices,
    cameraOn,
    handleCameraDeviceChange,
    handleMicrophoneDeviceChange,
    handleSpeakerDeviceChange,
    handleCameraSwitch,
    isConnected,
    localCameraTrack,
    localMicrophoneTrack,
    micOn,
    microphoneDevices,
    remoteUsers,
    speakerDevices,
    setMic,
    setCamera,
    setIsUserReady,
    isPageReady,
    isLoading,

    // New (D1 partner-app billing wiring):
    meeting,       // populated /api/meeting/by-id payload (user, mode, etc)
    meetingId,     // raw meetingId from URL — handy for screens
    duration,      // wall-clock seconds since isConnected went true
    billing,       // partner-scoped billing snapshot { isActive, rate, billed }
    endCall,       // ordered teardown + end-of-call routing
  };
};
